// eslint-disable-next-line vue/valid-v-slot
<template>
    <v-container fluid class="pa-0">
        <v-row>
            <v-col>
                <v-card>
                    <v-card-title> Requests </v-card-title>
                </v-card>
                <v-data-table
                    :headers="headers"
                    :items="requests"
                    class="elevation-1"
                    clearable
                    :disabled="isLoading"
                    :loading="isLoading"
                    :items-per-page="25"
                    :search="search"
                    no-results-text="No matching records found (you may have to clear the protocol or status filter in the table header)"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.request_registry_id="{ item, value }">
                        <a :href="item.request_url" target="_blank">{{ value }}</a>
                    </template>
                    <template v-slot:item.src_experiment_code="{ item }">
                        <div v-for="(exp, index) in item.src_experiment_code" :key="index">
                            <a :href="item.entry_url[index]" target="_blank">
                                {{ exp }}
                            </a>
                        </div>
                    </template>

                    <!-- Filter 'Protocols' column -->
                    <template v-slot:header.name>
                        <div style="max-width: 200px">
                            <v-autocomplete
                                class="text-same-as-other-headers"
                                v-model="protocolId"
                                @update:model-value="onFilterChange"
                                :items="protocolOptions"
                                item-title="name"
                                item-value="protocol_id"
                                clearable
                                placeholder="Protocols (All)"
                            >
                            </v-autocomplete>
                        </div>
                    </template>

                    <!-- Filter 'status' column -->
                    <template v-slot:header.status>
                        <v-autocomplete
                            class="text-same-as-other-headers"
                            v-model="status"
                            @update:model-value="onFilterChange"
                            :items="filterStatuses"
                            item-title="name"
                            item-value="name"
                            clearable
                            placeholder="Status (All)"
                        >
                        </v-autocomplete>
                    </template>

                    <!-- Inline editing for item status -->
                    <template v-slot:item.status="{ item }">
                        <div v-if="isAuthorized('Requests:Update')">
                            <v-autocomplete
                                class="text-same-as-other-headers"
                                v-model="item.status"
                                @update:model-value="
                                    onReqStatusChange(item.status, item.request_id)
                                "
                                :items="filterStatuses"
                                item-title="name"
                            >
                            </v-autocomplete>
                        </div>
                        <div v-else>
                            {{ item.status }}
                        </div>
                    </template>

                    <!-- 'Actions' column -->
                    <template v-slot:item.actions="{ item }">
                        <v-icon-btn
                            v-if="isEditable(item)"
                            color="primary"
                            @click="edit(item.request_registry_id)"
                            :id="`edit${item.request_registry_id}`"
                        >
                            <v-icon>$pencil</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#edit${item.request_registry_id}`"
                            >
                                <span>Edit Request</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            v-if="item.number_of_plates > 0"
                            color="primary"
                            @click="showPlateMapDiagramDialog(item)"
                            :id="`showPlateMap${item.request_registry_id}`"
                        >
                            <v-icon>$view-grid-compact</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#showPlateMap${item.request_registry_id}`"
                            >
                                <span>View Plate Map</span>
                            </v-tooltip>
                        </v-icon-btn>
                    </template>

                    <!-- 'Downloads' column -->
                    <template v-slot:item.downloads="{ item }">
                        <v-icon-btn
                            v-if="
                                item.status !== 'Samples' &&
                                item.functional_team[0] !== 'Analytical' &&
                                item.number_of_plates > 0
                            "
                            color="primary"
                            @click="download384(item.request_registry_id)"
                            :id="`download384${item.request_registry_id}`"
                            :loading="isDownloading === item.request_registry_id + '384'"
                        >
                            <v-icon>$apps-box</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#download384${item.request_registry_id}`"
                            >
                                <span>384-Well Setup File</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            color="primary"
                            @click="downloadSampleConfigTemplate(item)"
                            :id="`downloadSampleConfigTemplate${item.request_registry_id}`"
                        >
                            <v-icon>$table-cog</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#downloadSampleConfigTemplate${item.request_registry_id}`"
                            >
                                <span>Sample Setup Template</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            v-if="supportsSampleDataUpload(item)"
                            color="primary"
                            @click="downloadSampleDataTemplate(item)"
                            :id="`downloadSampleDataTemplate${item.request_registry_id}`"
                        >
                            <v-icon>$table-arrow-down</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#downloadSampleDataTemplate${item.request_registry_id}`"
                            >
                                <span>Sample Data Template</span>
                            </v-tooltip>
                        </v-icon-btn>
                    </template>

                    <!-- 'Uploads' column -->
                    <template v-slot:item.uploads="{ item }">
                        <v-icon-btn
                            color="primary"
                            @click="showUploadSampleSetupTemplateDialog(item)"
                            :id="`uploadSampleSetupTemplate${item.request_registry_id}`"
                        >
                            <v-icon>$cog</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#uploadSampleSetupTemplate${item.request_registry_id}`"
                            >
                                <span>Upload Sample Setup Template</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            v-if="supportsPlateUpload(item)"
                            color="primary"
                            @click="uploadPlateData(item)"
                            :id="`uploadPlateData${item.request_registry_id}`"
                        >
                            <v-icon>$upload</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#uploadPlateData${item.request_registry_id}`"
                            >
                                <span>Upload Plate Data</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            v-if="supportsSampleDataUpload(item)"
                            color="primary"
                            @click="showUploadSampleDataDialog(item)"
                            :id="`uploadSampleData${item.request_registry_id}`"
                        >
                            <v-icon>$table-arrow-up</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#uploadSampleData${item.request_registry_id}`"
                            >
                                <span>Upload Sample Data</span>
                            </v-tooltip>
                        </v-icon-btn>
                        <v-icon-btn
                            v-if="supportsImageUpload(item)"
                            color="primary"
                            @click="showUploadImageDialog(item)"
                            :id="`uploadImage${item.request_registry_id}`"
                        >
                            <v-icon>$camera</v-icon>
                            <v-tooltip
                                location="right"
                                :activator="`#uploadImage${item.request_registry_id}`"
                            >
                                <span>Upload Images</span>
                            </v-tooltip>
                        </v-icon-btn>
                    </template>
                </v-data-table>

                <req-dialog v-model="data" :show="isOpen" @onSave="saved"> </req-dialog>
                <PlateReaderUploadDialog
                    :dialog="showPlateUploadDialog"
                    :code="uploadRequest"
                    codeType="request"
                    :plates="requestPlates"
                    v-if="showPlateUploadDialog"
                    @cancel="cancelPlateUploadDialog"
                />
                <v-dialog v-model="showSampleDataUploadDialog" max-width="600px">
                    <Upload-Request-File
                        v-model="sampleDataFile"
                        :requestCode="uploadRequest"
                        fileDescription="Sample Data"
                        @close="closeUploadSampleDataDialog"
                        @submit="uploadSampleData"
                    />
                </v-dialog>
                <v-dialog v-model="showRequestImageUploadDialog" max-width="800px">
                    <Upload-Request-Images-Wizard
                        v-model="requestImageFile"
                        :request="imageUploadRequest"
                        @close="closeRequestImageUploadDialog"
                        @submit="uploadRequestImages"
                    />
                </v-dialog>
                <v-dialog v-model="showSampleSetupTemplateUploadDialog" max-width="600px">
                    <Upload-Request-File
                        v-model="sampleSetupTemplateFile"
                        :requestCode="uploadRequest"
                        fileDescription="Sample Setup Template"
                        @close="closeSampleSetupTemplateUploadDialog"
                        @submit="uploadSampleSetupTemplate"
                    />
                </v-dialog>
                <v-dialog
                    v-model="showPlateMapDialog"
                    @click:outside="closePlateMapDiagramDialog"
                    @keyup.esc="closePlateMapDiagramDialog"
                    width="unset"
                >
                    <Request-Plate-Map
                        :requestCode="plateMapRequest"
                        :plateMap="plateMap"
                        @close="closePlateMapDiagramDialog"
                    />
                </v-dialog>

                <v-snackbar
                    v-model="updateAlert.status"
                    :timeout="3000"
                    :location="true ? 'top' : undefined"
                >
                    {{ updateAlert.message }}
                </v-snackbar>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "@/lib/apiAxios";
import { mapActions, mapGetters } from "vuex";
import { download384Excel, requestsAPI } from "@/services/api.services";
import {
    getSamplesDataTemplateUrl,
    uploadSampleDataFile,
    uploadSampleSetupFile,
    workflowSupportsSampleDataTemplate,
    workflowSupportsPlateUpload,
    workflowSupportsImageUpload,
} from "@/lib/samplesUtils";
import ReqDialog from "./ReqDialog";
import PlateReaderUploadDialog from "../Wells/PlateReaderUploadDialog";
import UploadRequestFile from "./UploadRequestFile";
import UploadRequestImagesWizard from "./UploadRequestImagesWizard";
import RequestPlateMap from "./RequestPlateMap";
import { withLoadingScreen } from "@/lib/loading";
import config from "@/config";
import { downloadFile } from "@/lib/downloadFileUtil.js";

const defaultProtocols = [
    { name: "All Analytical", protocol_id: "analytical" },
    { name: "All Molecular", protocol_id: "molecular" },
];

export default {
    name: "RequestsList",
    components: {
        PlateReaderUploadDialog,
        ReqDialog,
        RequestPlateMap,
        UploadRequestFile,
        UploadRequestImagesWizard,
    },
    props: {
        search: {
            type: String,
            default: "2",
        },
        tableHeaderColor: {
            type: String,
            default: "",
        },
    },
    async created() {
        await withLoadingScreen(async () => {
            await this.initialize();
            await this.fetchRequests({ statusId: this.status, protocolId: this.protocolId });
            this.protocolOptions = defaultProtocols.concat(this.protocols);
        });
    },
    computed: {
        ...mapGetters("requests", ["isLoading", "requests", "statuses"]),
        ...mapGetters(["isAuthorized"]),
        protocols() {
            return this.$store.state.requests.protocols;
        },
        filterStatuses() {
            return this.statuses.filter((status) => status.name !== "Samples");
        },
    },
    methods: {
        ...mapActions("requests", ["fetchRequests", "initialize"]),
        isEditable(item) {
            return this.isAuthorized("Requests:Update") && item.number_of_plates > 0;
        },
        onFilterChange() {
            localStorage.setItem(
                "requests.fetch.filter",
                JSON.stringify({ status: this.status, protocolId: this.protocolId })
            );
            this.fetchRequests({ statusId: this.status, protocolId: this.protocolId });
        },
        supportsPlateUpload(item) {
            const sampleWorkflow = this.getSampleWorkflow(item);
            return workflowSupportsPlateUpload(sampleWorkflow);
        },
        supportsSampleDataUpload(item) {
            const sampleWorkflow = this.getSampleWorkflow(item);
            return workflowSupportsSampleDataTemplate(sampleWorkflow);
        },
        getSampleWorkflow(item) {
            return this.protocols.find((p) => p.protocol_id === item.protocol_id[0])
                .sample_workflow;
        },
        supportsImageUpload(item) {
            const sampleWorkflow = this.getSampleWorkflow(item);
            return workflowSupportsImageUpload(sampleWorkflow);
        },
        async onReqStatusChange(newStatus, reqId) {
            const schemaResponse = await axios.get(`/benchling/get_entity_schema_data/${reqId}`);
            const schemaId = schemaResponse.data[0][0].schema_id;

            const statusMap = new Map();
            const dropdownResponse = await axios.get(`/benchling/dropdowns/Request Status`);
            dropdownResponse.data.map((status) => statusMap.set(status.name, status.id));

            const schemaUpdate = {
                fields: {
                    Status: { value: statusMap.get(newStatus) },
                },
                schemaId,
            };

            try {
                console.log("Updating REQ Status");
                const update = await axios.put(
                    `/benchling/updateCustomEntity/${reqId}`,
                    schemaUpdate
                );
                if (update.status === 200) {
                    console.log("REQ Status successfully updated.");
                    this.updateAlert = {
                        status: true,
                        message:
                            "REQ Status successfully updated. (It may take a minute for changes to show)",
                    };
                }
            } catch (err) {
                console.error(err);
                this.updateAlert = {
                    status: true,
                    message: `There was an error during update. ${err}`,
                };
            }
        },
        saved: function (data) {
            this.isOpen = false;
        },
        link: function (data) {
            this.data = data;
            this.isOpen = true;
        },
        async download384(requestCode) {
            this.isDownloading = requestCode + "384";
            const response = await download384Excel(
                requestCode,
                `${requestCode}-384-Plate-Setup.xlsx`
            );
            this.isDownloading = null;
            downloadFile(`${requestCode}-384-Plate-Setup.xlsx`, response);
        },
        downloadSampleDataTemplate(item) {
            const reqProtocol = this.getRequestProtocol(item.protocol_id[0]);
            const templateUrl = getSamplesDataTemplateUrl(
                item.request_registry_id,
                reqProtocol.code,
                item.src_experiment_code[0]
            );
            window.open(templateUrl, "_blank");
        },
        downloadSampleConfigTemplate(item) {
            const requestCode = item.request_registry_id;
            window.open(`${config.apiHost}/requests/setup/${requestCode}`, "_blank");
        },
        edit(requestCode) {
            this.$router.push({ name: "BuildPlate", params: { requestCode } });
        },
        uploadPlateData(request) {
            const plate_ids = request.plate_ids;
            this.requestPlates = plate_ids.map((plate_id, index) => ({
                id: plate_id,
                name: request.plate_names[index],
                plateGroup: "Plates",
            }));
            this.uploadRequest = request.request_registry_id;
            this.showPlateUploadDialog = true;
        },
        showUploadSampleDataDialog(request) {
            this.uploadRequest = request.request_registry_id;
            this.showSampleDataUploadDialog = true;
        },
        showUploadImageDialog(request) {
            this.imageUploadRequest = request;
            this.showRequestImageUploadDialog = true;
        },
        showUploadSampleSetupTemplateDialog(request) {
            this.uploadRequest = request.request_registry_id;
            this.showSampleSetupTemplateUploadDialog = true;
        },
        async showPlateMapDiagramDialog(request) {
            this.plateMapRequest = request.request_registry_id;
            await withLoadingScreen(async () => {
                const plateMap = await requestsAPI.fetchPlateMap(this.plateMapRequest);
                this.plateMap = plateMap;
            });
            this.showPlateMapDialog = true;
        },
        closeRequestImageUploadDialog() {
            this.showRequestImageUploadDialog = false;
        },
        uploadRequestImages(file) {
            this.showRequestImageUploadDialog = false;
        },
        closeSampleSetupTemplateUploadDialog() {
            this.showSampleSetupTemplateUploadDialog = false;
        },
        cancelPlateUploadDialog() {
            this.showPlateUploadDialog = false;
        },
        closeUploadSampleDataDialog() {
            this.showSampleDataUploadDialog = false;
        },
        closePlateMapDiagramDialog() {
            this.showPlateMapDialog = false;
            this.plateMapPlates = [];
            this.plateMapRequest = null;
        },
        async uploadSampleData(file) {
            await uploadSampleDataFile(file, this.uploadRequest);
            this.showSampleDataUploadDialog = false;
        },
        async uploadSampleSetupTemplate(file) {
            await uploadSampleSetupFile(file, this.uploadRequest);
            this.showSampleSetupTemplateUploadDialog = false;
        },
        getRequestProtocol(protocolId) {
            return this.protocols.find((protocol) => protocol.protocol_id === protocolId);
        },
    },
    data() {
        return {
            data: null,
            isOpen: false,
            isDownloading: null,
            updateAlert: {
                status: false,
                message: "",
            },
            headers: [
                {
                    title: "Req Id",
                    value: "request_registry_id",
                    align: "left",
                    sortable: true,
                    width: "84",
                },
                {
                    title: "Source EXP",
                    value: "src_experiment_code",
                    align: "left",
                    sortable: true,
                    width: "120",
                },
                {
                    title: "# Plates",
                    value: "number_of_plates",
                    align: "center",
                    width: "50",
                },
                {
                    title: "# Samples",
                    value: "number_of_samples",
                    align: "center",
                    width: "50",
                },
                {
                    title: "Date Submitted",
                    value: "created_date",
                    align: "left",
                    width: "110",
                },
                {
                    title: "Date Required",
                    value: "date_required",
                    align: "left",
                    width: "110",
                },
                {
                    title: "Protocol",
                    value: "name",
                    align: "left",
                    sortable: false,
                    width: "200",
                },
                {
                    title: "Status",
                    value: "status",
                    align: "left",
                    sortable: false,
                    width: "130",
                },
                {
                    title: "Actions",
                    value: "actions",
                    sortable: false,
                    width: "140",
                },
                {
                    title: "Downloads",
                    value: "downloads",
                    sortable: false,
                    width: "140",
                },
                {
                    title: "Uploads",
                    value: "uploads",
                    sortable: false,
                    width: "180",
                },
            ],
            rowsPerPageItems: [20, 40, 60],
            pagination: {
                rowsPerPage: 20,
            },
            status: "Open",
            protocolId: null,
            requestPlates: [],
            uploadRequest: null,
            imageUploadRequest: null,
            showPlateUploadDialog: false,
            showSampleDataUploadDialog: false,
            showRequestImageUploadDialog: false,
            showSampleSetupTemplateUploadDialog: false,
            showPlateMapDialog: false,
            plateMapRequest: null,
            plateMap: {},
            sampleDataFile: null,
            sampleSetupTemplateFile: null,
            requestImageFile: null,
            protocolOptions: [],
        };
    },
};
</script>

<style>
.text-same-as-other-headers {
    font-size: 0.75rem !important;
}
</style>
